import {
  AlignmentType,
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { fetchImageProxyArrayBuffer } from '@taleemabad/db';

export interface ExamDocxSubQuestion {
  text: string;
  marks: number;
  lines: number;
}

export interface ExamDocxQuestion {
  question: string;
  marks: number;
  mainQuestion?: string;
  answerLines?: number;
  subQuestions?: ExamDocxSubQuestion[];
  image?: string;
  column_a?: string[];
  column_b?: string[];
}

export interface ExamDocxSection {
  title: string;
  marks: number;
  questions: ExamDocxQuestion[];
}

export interface ExamDocxOptions {
  schoolName?: string;
  gradeSubject: string;
  totalMarks: number;
  instructions: string[];
  showAnswerLines: boolean;
  questionSections: ExamDocxSection[];
  isUrdu?: boolean;
  fontSize?: number;
}

// ─── page layout ─────────────────────────────────────────────────────────────
// A4: 11906 x 16838 twips. Margins: 1440 each side.
// Usable content width: 11906 - 2880 = 9026 twips.
const CONTENT_WIDTH = 9026;
const MARGIN = 1440;

// Table column widths (4 cols summing to CONTENT_WIDTH)
const COL_LABEL = 2100; // label cells: "Student Name", "Roll No", etc.
const COL_VALUE = Math.floor((CONTENT_WIDTH - COL_LABEL * 2) / 2); // value cells
// col1=2100, col2=2413, col3=2100, col4=2413 → total=9026

// ─── text helpers ─────────────────────────────────────────────────────────────

function run(
  text: string,
  opts: Partial<{
    bold: boolean;
    italics: boolean;
    size: number;
    isUrdu?: boolean;
    fontSizeScale?: number;
  }> = {},
): TextRun {
  const { isUrdu, fontSizeScale = 1, ...restOpts } = opts;
  const font = isUrdu ? 'Noto Nastaliq Urdu' : 'Arial';
  const scaledSize = restOpts.size
    ? Math.round(restOpts.size * fontSizeScale)
    : undefined;
  return new TextRun({
    text,
    font,
    ...restOpts,
    ...(scaledSize ? { size: scaledSize } : {}),
  });
}

function spacer(): Paragraph {
  return new Paragraph({ text: '', spacing: { after: 80 } });
}

/** A bottom-bordered empty paragraph simulating an answer line. */
function answerLine(): Paragraph {
  return new Paragraph({
    text: '',
    border: {
      bottom: { color: '000000', space: 1, style: BorderStyle.SINGLE, size: 6 },
    },
    spacing: { before: 0, after: 200 },
  });
}

// ─── student info table ───────────────────────────────────────────────────────

const CELL_BORDERS = {
  top: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
  bottom: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
  left: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
  right: { style: BorderStyle.SINGLE, size: 4, color: '000000' },
};

const CELL_MARGINS = { top: 100, bottom: 100, left: 150, right: 150 };

function labelCell(text: string, fontSizeScale = 1): TableCell {
  return new TableCell({
    children: [
      new Paragraph({
        children: [run(text, { bold: true, size: 20, fontSizeScale })],
        spacing: { before: 60, after: 60 },
      }),
    ],
    borders: CELL_BORDERS,
    margins: CELL_MARGINS,
    width: { size: COL_LABEL, type: WidthType.DXA },
  });
}

// Span width for cells starting at a COL_VALUE column:
// cols alternate [COL_LABEL, COL_VALUE, COL_LABEL, COL_VALUE]
// span=1 → COL_VALUE; span=2 → COL_VALUE+COL_LABEL; span=3 → COL_VALUE+COL_LABEL+COL_VALUE
// General: ceil(span/2)*COL_VALUE + floor(span/2)*COL_LABEL
function valueCellWidth(span: number): number {
  return Math.ceil(span / 2) * COL_VALUE + Math.floor(span / 2) * COL_LABEL;
}

function valueCell(text = '', span = 1, fontSizeScale = 1): TableCell {
  return new TableCell({
    children: [
      new Paragraph({
        children: [run(text, { size: 20, fontSizeScale })],
        spacing: { before: 60, after: 60 },
      }),
    ],
    columnSpan: span > 1 ? span : undefined,
    borders: CELL_BORDERS,
    margins: CELL_MARGINS,
    width: { size: valueCellWidth(span), type: WidthType.DXA },
  });
}

function buildStudentTable(totalMarks: number, fontSizeScale = 1): Table {
  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [COL_LABEL, COL_VALUE, COL_LABEL, COL_VALUE],
    rows: [
      new TableRow({
        children: [
          labelCell('Student Name', fontSizeScale),
          valueCell('', 3, fontSizeScale),
        ],
      }),
      new TableRow({
        children: [
          labelCell('Roll No', fontSizeScale),
          valueCell('', 1, fontSizeScale),
          labelCell('Date', fontSizeScale),
          valueCell('', 1, fontSizeScale),
        ],
      }),
      new TableRow({
        children: [
          labelCell('Total Marks', fontSizeScale),
          valueCell(String(totalMarks), 1, fontSizeScale),
          labelCell('Obtained Marks', fontSizeScale),
          valueCell('', 1, fontSizeScale),
        ],
      }),
    ],
  });
}

// ─── instructions box ─────────────────────────────────────────────────────────

// Use a single-cell Table so all four borders form one clean unbroken rectangle.
function buildInstructionsBox(
  instructions: string[],
  fontSizeScale = 1,
): Table {
  const BOX_BORDER = {
    top: { style: BorderStyle.SINGLE, size: 6, color: '888888' },
    bottom: { style: BorderStyle.SINGLE, size: 6, color: '888888' },
    left: { style: BorderStyle.SINGLE, size: 6, color: '888888' },
    right: { style: BorderStyle.SINGLE, size: 6, color: '888888' },
    insideH: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    insideV: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  };

  const cellChildren: Paragraph[] = [
    new Paragraph({
      children: [run('Instructions', { bold: true, size: 20, fontSizeScale })],
      spacing: { before: 60, after: 80 },
    }),
    ...instructions.map(
      (item) =>
        new Paragraph({
          children: [run(`\u2022  ${item}`, { size: 18, fontSizeScale })],
          spacing: { before: 40, after: 40 },
          indent: { left: 160 },
        }),
    ),
  ];

  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [CONTENT_WIDTH],
    borders: BOX_BORDER,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            children: cellChildren,
            margins: { top: 120, bottom: 120, left: 180, right: 180 },
            borders: BOX_BORDER,
          }),
        ],
      }),
    ],
  });
}

// ─── image helpers ────────────────────────────────────────────────────────────

const IMG_MAX_WIDTH_PX = 400;

async function fetchImageData(url: string): Promise<{
  data: ArrayBuffer;
  width: number;
  height: number;
  mimeType: string;
} | null> {
  try {
    const result = await fetchImageProxyArrayBuffer(url);
    if (!result) {
      console.warn(
        `[generateExamDocx] Image proxy returned empty response for: ${url}`,
      );
      return null;
    }
    const { data: arrayBuffer, contentType: mimeType } = result;

    const blob = new Blob([arrayBuffer], { type: mimeType });
    const imageBitmap = await createImageBitmap(blob);
    const scale = Math.min(1, IMG_MAX_WIDTH_PX / imageBitmap.width);
    const width = Math.round(imageBitmap.width * scale);
    const height = Math.round(imageBitmap.height * scale);
    imageBitmap.close();
    return { data: arrayBuffer, width, height, mimeType };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error(
      `[generateExamDocx] Failed to fetch image from proxy for URL: ${url}`,
      errorMsg,
    );
    return null;
  }
}

async function prefetchImages(
  questionSections: ExamDocxSection[],
): Promise<
  Map<
    string,
    { data: ArrayBuffer; width: number; height: number; mimeType: string }
  >
> {
  const urls = new Set<string>();
  for (const section of questionSections) {
    for (const q of section.questions) {
      if (q.image) urls.add(q.image);
    }
  }
  const map = new Map<
    string,
    { data: ArrayBuffer; width: number; height: number; mimeType: string }
  >();
  const fetchResults = await Promise.all(
    Array.from(urls).map(async (url) => {
      const result = await fetchImageData(url);
      if (result) map.set(url, result);
      return { url, success: !!result };
    }),
  );

  // Log summary of image fetch results
  const failed = fetchResults.filter((r) => !r.success);
  if (failed.length > 0) {
    console.warn(
      `[generateExamDocx] ${failed.length}/${urls.size} images failed to fetch. ` +
        `Check browser console for details.`,
    );
  } else if (urls.size > 0) {
    console.info(
      `[generateExamDocx] Successfully fetched ${urls.size} images for DOCX`,
    );
  }

  return map;
}

// ─── match the columns table ─────────────────────────────────────────────────

function buildMatchColumnsTable(
  columnA: string[],
  columnB: string[],
  isUrdu = false,
  fontSizeScale = 1,
): Table {
  const maxRows = Math.max(columnA.length, columnB.length);
  const COL_HALF = CONTENT_WIDTH / 2;

  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({
            children: [
              run(isUrdu ? 'کالم (الف)' : 'Column A', {
                bold: true,
                size: 20,
                fontSizeScale,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 60, after: 60 },
          }),
        ],
        borders: CELL_BORDERS,
        margins: CELL_MARGINS,
        width: { size: COL_HALF, type: WidthType.DXA },
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [
              run(isUrdu ? 'کالم (ب)' : 'Column B', {
                bold: true,
                size: 20,
                fontSizeScale,
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 60, after: 60 },
          }),
        ],
        borders: CELL_BORDERS,
        margins: CELL_MARGINS,
        width: { size: COL_HALF, type: WidthType.DXA },
      }),
    ],
  });

  const dataRows = Array.from({ length: maxRows }).map((_, idx) => {
    return new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [run(columnA[idx] || '', { size: 20, fontSizeScale })],
              spacing: { before: 60, after: 60 },
              alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
              bidi: isUrdu,
            } as any),
          ],
          borders: CELL_BORDERS,
          margins: CELL_MARGINS,
          width: { size: COL_HALF, type: WidthType.DXA },
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [run(columnB[idx] || '', { size: 20, fontSizeScale })],
              spacing: { before: 60, after: 60 },
              alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
              bidi: isUrdu,
            } as any),
          ],
          borders: CELL_BORDERS,
          margins: CELL_MARGINS,
          width: { size: COL_HALF, type: WidthType.DXA },
        }),
      ],
    });
  });

  return new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    rows: [headerRow, ...dataRows],
  });
}

// ─── question section ─────────────────────────────────────────────────────────

function buildSection(
  section: ExamDocxSection,
  sectionIndex: number,
  showAnswerLines: boolean,
  imageCache: Map<
    string,
    { data: ArrayBuffer; width: number; height: number; mimeType: string }
  >,
  isUrdu = false,
  fontSizeScale = 1,
): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  // Section header: "Q.1. Choose the correct option    (3)" or "Q.1 درست جواب کا انتخاب کریں    (3)"
  // For Urdu, content flows right-to-left, so Q.1 appears on the right
  // Marks appear right-aligned via a right-aligned tab stop at content width
  const sectionTitle = section.questions[0]?.mainQuestion || section.title;
  const qLabel = `Q.${sectionIndex + 1}`;

  paragraphs.push(
    new Paragraph({
      children: isUrdu
        ? [
            run(`${sectionTitle}`, {
              bold: true,
              size: 20,
              isUrdu,
              fontSizeScale,
            }),
            run(' ', { isUrdu }),
            run(qLabel, { bold: true, size: 20, isUrdu, fontSizeScale }),
            run('\t', { isUrdu }),
            run(`(${section.marks})`, { size: 20, isUrdu, fontSizeScale }),
          ]
        : [
            run(qLabel, { bold: true, size: 20, isUrdu, fontSizeScale }),
            run(`. ${sectionTitle}`, {
              bold: true,
              size: 20,
              isUrdu,
              fontSizeScale,
            }),
            run('\t', { isUrdu }),
            run(`(${section.marks})`, { size: 20, isUrdu, fontSizeScale }),
          ],
      spacing: { before: 200, after: 120 },
      tabStops: isUrdu
        ? [{ type: 'left', position: 300 }]
        : [{ type: 'right', position: CONTENT_WIDTH }],
      alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
      bidi: isUrdu,
    } as any),
  );

  section.questions.forEach((q) => {
    // Check if this is a match-the-columns question
    const isMatchColumns =
      q.column_a &&
      q.column_b &&
      Array.isArray(q.column_a) &&
      Array.isArray(q.column_b) &&
      q.column_a.length > 0 &&
      q.column_b.length > 0;

    if (isMatchColumns) {
      // For match-the-columns questions, render the instruction (question) first
      // Extract only the instruction text before any column data
      const text = q.question ?? '';

      // Find where the column data starts
      const columnStartIndex = Math.max(
        text.search(/column\s*a/i),
        text.search(/کالم\s*\(الف\)/),
      );

      // Get instruction: everything before column data, or full text if no column pattern found
      let instruction = text;
      if (columnStartIndex > 0) {
        instruction = text.substring(0, columnStartIndex).trim();
      }

      // Render instruction only if it exists and doesn't contain column labels
      if (instruction && !/(?:column|کالم)/i.test(instruction)) {
        paragraphs.push(
          new Paragraph({
            children: [run(instruction, { size: 20, isUrdu, fontSizeScale })],
            indent: isUrdu ? { right: 200, left: 0 } : { left: 200 },
            spacing: { before: 100, after: 200 },
            alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
            bidi: isUrdu,
          } as any),
        );
      }

      // Add the match columns table as a separate element
      if (q.column_a && q.column_b) {
        const tableElement = buildMatchColumnsTable(
          q.column_a,
          q.column_b,
          isUrdu,
          fontSizeScale,
        );
        (paragraphs as any).push(tableElement);
      }

      // Add spacing after table
      paragraphs.push(spacer());
    } else {
      // Regular question rendering (split by newlines)
      const questionText = q.question ?? '';
      const lines = questionText.split('\n');

      lines.forEach((line, lineIdx) => {
        if (!line.trim()) return;
        paragraphs.push(
          new Paragraph({
            children: [
              run(line, {
                size: lineIdx === 0 ? 20 : 18,
                isUrdu,
                fontSizeScale,
              }),
            ],
            indent: isUrdu
              ? { right: lineIdx === 0 ? 200 : 400, left: 0 }
              : { left: lineIdx === 0 ? 200 : 400 },
            spacing: { before: lineIdx === 0 ? 100 : 20, after: 20 },
            alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
            bidi: isUrdu,
          } as any),
        );
      });
    }

    // Image (if present)
    if (q.image) {
      const cached = imageCache.get(q.image);
      if (cached) {
        paragraphs.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: cached.data,
                transformation: { width: cached.width, height: cached.height },
                type: cached.mimeType.includes('png') ? 'png' : 'jpg',
              }),
            ],
            alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
            indent: isUrdu ? { right: 200 } : { left: 200 },
            spacing: { before: 80, after: 80 },
          }),
        );
      }
    }

    // Sub-questions (e.g. comprehension passage sub-items)
    if (q.subQuestions && q.subQuestions.length > 0) {
      q.subQuestions.forEach((sq, si) => {
        const subText = sq.text ?? '';
        paragraphs.push(
          new Paragraph({
            children: [
              run(`${si + 1}. ${subText}`, {
                size: 20,
                isUrdu,
                fontSizeScale,
              }),
            ],
            indent: isUrdu ? { right: 400, left: 0 } : { left: 400 },
            spacing: { before: 80, after: 20 },
            alignment: isUrdu ? AlignmentType.RIGHT : AlignmentType.LEFT,
            bidi: isUrdu,
          } as any),
        );
        if (showAnswerLines && sq.lines > 0) {
          for (let l = 0; l < sq.lines; l++) paragraphs.push(answerLine());
        }
      });
    } else if (
      showAnswerLines &&
      q.answerLines !== undefined &&
      q.answerLines > 0
    ) {
      for (let l = 0; l < q.answerLines; l++) paragraphs.push(answerLine());
    }
  });

  return paragraphs;
}

// ─── main export ──────────────────────────────────────────────────────────────

export async function generateExamDocx(
  options: ExamDocxOptions,
): Promise<Blob> {
  const {
    schoolName,
    gradeSubject,
    totalMarks,
    instructions,
    showAnswerLines,
    questionSections,
    isUrdu = false,
    fontSize = 12,
  } = options;

  const fontSizeScale = fontSize / 12;

  const imageCache = await prefetchImages(questionSections);
  const children: (Paragraph | Table)[] = [];

  // 1. School name — centered, large bold
  if (schoolName) {
    children.push(
      new Paragraph({
        children: [run(schoolName, { bold: true, size: 36, fontSizeScale })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 60 },
      }),
    );
  }

  // 2. Grade / Subject — centered, smaller
  children.push(
    new Paragraph({
      children: [run(gradeSubject, { size: 24, fontSizeScale })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    }),
  );

  // 3. Student info table + 4. Instructions box
  children.push(
    buildStudentTable(totalMarks, fontSizeScale),
    spacer(),
    buildInstructionsBox(instructions, fontSizeScale),
    spacer(),
  );

  // 5. Question sections
  questionSections.forEach((section, idx) => {
    buildSection(
      section,
      idx,
      showAnswerLines,
      imageCache,
      isUrdu,
      fontSizeScale,
    ).forEach((p) => children.push(p));
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: { width: 11906, height: 16838 }, // A4
            margin: {
              top: MARGIN,
              right: MARGIN,
              bottom: MARGIN,
              left: MARGIN,
            },
          },
        },
        children,
      },
    ],
  });

  return Packer.toBlob(doc);
}
